import { Injectable } from '@angular/core';
import { PDFDocument } from 'pdf-lib';
import {
  SIGNATURE_BOX_HEIGHT_PT,
  SIGNATURE_BOX_WIDTH_PT,
} from '../shared-components/signature-box.util';

@Injectable({
  providedIn: 'root',
})
export class ImageGeneratorService {
  // Blank frame for an authorized  Aadhaar/DSC signatory: "For: {name}" on
  // top, empty middle for the eventual emudhra signature, "Authorised
  // Signatory" on the bottom. Pixel size doesn't need to match the final
  // placement box — pdf-lib stretches it to fit when drawn onto the page.
  generateAuthorisedFrame(authorisedFor: string): string {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 160;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.textAlign = 'left';
    ctx.fillStyle = '#1a56db';

    const forText = `For ${authorisedFor}`;
    this.fitBoldFont(ctx, forText, 24, canvas.width - 10);
    ctx.textBaseline = 'top';
    ctx.fillText(forText, 5, 5);

    ctx.font = 'bold 22px Arial';
    ctx.textBaseline = 'bottom';
    ctx.fillText('Authorised Signatory', 5, canvas.height - 5);

    return canvas.toDataURL('image/png');
  }

  // Bold Arial at `maxSize`, shrunk only as far as needed for `text` to fit
  // in `maxWidth`, so a long "Authorised For" name is never cut off.
  private fitBoldFont(ctx: CanvasRenderingContext2D, text: string, maxSize: number, maxWidth: number): void {
    ctx.font = `bold ${maxSize}px Arial`;
    const width = ctx.measureText(text).width;
    if (width <= maxWidth) {
      return;
    }
    let size = Math.floor((maxSize * maxWidth) / width);
    ctx.font = `bold ${size}px Arial`;
    while (size > 1 && ctx.measureText(text).width > maxWidth) {
      size -= 1;
      ctx.font = `bold ${size}px Arial`;
    }
  }

  generateAuthorisedFrameWithSignature(base64Image: string, authorisedFor: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 500;
        canvas.height = 260;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Failed to get canvas context');
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const topLabelHeight = 32;
        const bottomLabelHeight = 32;
        const sideMargin = 5;
        const availableWidth = canvas.width - sideMargin * 2;
        const availableHeight = canvas.height - topLabelHeight - bottomLabelHeight;

        const scale = Math.min(availableWidth / image.width, availableHeight / image.height);
        const drawWidth = image.width * scale;
        const drawHeight = image.height * scale;
        const drawX = (canvas.width - drawWidth) / 2;
        const drawY = topLabelHeight + (availableHeight - drawHeight) / 2;

        ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);

        ctx.fillStyle = '#1a56db';
        ctx.textAlign = 'left';

        const forText = `For ${authorisedFor}`;
        this.fitBoldFont(ctx, forText, 34, canvas.width - sideMargin * 2);
        ctx.textBaseline = 'top';
        ctx.fillText(forText, 5, 3);

        ctx.font = 'bold 32px Arial';
        ctx.textBaseline = 'bottom';
        ctx.fillText('Authorised Signatory', 5, canvas.height - 3);

        resolve(canvas.toDataURL('image/png'));
      };

      image.onerror = () => {
        reject('Failed to load image');
      };

      image.src = base64Image;
    });
  }

  async mergeAuthorisedFramesIntoPdf(pdfBase64: string, parties: any[]): Promise<string> {
    const authorisedParties = (parties || []).filter(
      (party: any) =>
        party?.is_authorised &&
        party?.authorised_for &&
        ['AADHAAR', 'DSC'].includes((party.method || '').toUpperCase()),
    );

    if (authorisedParties.length === 0) {
      return pdfBase64;
    }

    // pdfBase64 sometimes arrives as a full `data:application/pdf;base64,...`
    // URI rather than raw base64, depending on the upload flow — strip the
    // prefix (and any stray whitespace) before decoding either way.
    const cleanedPdfBase64 = (pdfBase64.includes(',') ? pdfBase64.split(',').pop() : pdfBase64).replace(/\s/g, '');
    const pdfBytes = Uint8Array.from(atob(cleanedPdfBase64), (c) => c.charCodeAt(0));
    const pdfDoc = await PDFDocument.load(pdfBytes);
    const pages = pdfDoc.getPages();

    for (const party of authorisedParties) {
      const frameDataUrl = this.generateAuthorisedFrame(party.authorised_for);
      const frameBytes = Uint8Array.from(
        atob(frameDataUrl.split(',')[1]),
        (c) => c.charCodeAt(0),
      );
      const embeddedImage = await pdfDoc.embedPng(frameBytes);

      for (const [pageKey, boxes] of Object.entries(party.position_details || {})) {
        const page = pages[Number(pageKey) - 1];
        if (!page || !Array.isArray(boxes)) continue;

        const pageWidth = page.getWidth();
        const pageHeight = page.getHeight();

        for (const box of boxes as any[]) {
          // One fixed absolute size for every placement type. Signature boxes
          // are now one common size too (see signature-box.util); the frame
          // stays independent of the box so its labels remain legible.
          let width = 140;
          let height = 65;

          const bottomPadding = 15;
          const x = box.x1;
          const y = box.y1 - bottomPadding;

          const fitScale = Math.min(
            1,
            (pageWidth - x) / width,
            (pageHeight - y) / height,
          );
          if (fitScale < 1) {
            width *= fitScale;
            height *= fitScale;
          }

          if (width > 0 && height > 0) {
            page.drawImage(embeddedImage, { x, y, width, height });
          }
        }
      }
    }

    return pdfDoc.saveAsBase64();
  }
  generateImage(
    text: string,
    style: string,
    width: number = 600,
    height: number = 250
  ) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');

    // Set semi-transparent white background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Set text properties
    ctx.font = style;
    ctx.fillStyle = 'black';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    // Draw text with a margin of 20 pixels from the left
    ctx.fillText(text, 20, canvas.height / 2);

    // Convert the canvas content to a data URL in PNG format with the specified background and text color
    return canvas.toDataURL('image/png');
  }

  async addBordersAndText(
    base64Image: string,
    topText: string,
    bottomText: string
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = () => {
        // Tight around the stamp, so it prints from the bottom of its box like
        // Aadhaar's. It used to be 600x200 with ~25px left empty under the ID
        // line, which lifted the electronic stamp above Aadhaar's.
        const canvas = document.createElement('canvas');
        canvas.width = 600;
        canvas.height = 180;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Failed to get canvas context');
          return;
        }

        // Clear canvas with transparent background
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Text lines are 36px (was 30px); a line too long for the width is
        // shrunk just enough to fit instead of being cut off.
        const textMaxWidth = canvas.width - 10;
        const setFittedFont = (text: string) => {
          let size = 36;
          ctx.font = `${size}px Cambria`;
          while (size > 20 && ctx.measureText(text).width > textMaxWidth) {
            size--;
            ctx.font = `${size}px Cambria`;
          }
        };

        // Draw the signature image between the two text lines (it slightly
        // overlaps the top line, as before).
        const imageTop = 24;
        const bottomTextHeight = 40;
        const imageHeight = canvas.height - imageTop - bottomTextHeight;
        ctx.drawImage(image, 0, imageTop, canvas.width, imageHeight);

        // Draw top text (no border)
        ctx.fillStyle = 'black';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        setFittedFont(topText);
        ctx.fillText(topText, 5, 0);

        // Draw left border only
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, imageTop);
        ctx.lineTo(0, imageTop + imageHeight);
        ctx.stroke();

        // Draw bottom text aligned to left border (no bottom border), resting on
        // the image's bottom edge so no blank band is left below it.
        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        setFittedFont(bottomText);
        ctx.fillText(bottomText, 5, canvas.height - 1);

        resolve(canvas.toDataURL('image/png'));
      };

      image.onerror = () => {
        reject('Failed to load image');
      };

      image.src = base64Image;
    });
  }

  /**
   * The copy of a stamp that gets placed on the PDF: padded with transparent
   * space to the signature box's own shape (SIGNATURE_BOX_WIDTH_PT x
   * SIGNATURE_BOX_HEIGHT_PT), the stamp resting on the bottom edge.
   *
   * The image is fitted into its box keeping its proportions and centred. The
   * 600x180 electronic stamp is wider than the 110x50 box, so it came out 110x33
   * and floated ~8.5pt above the box bottom, while an Aadhaar stamp sits on it.
   * With the box's shape it still prints 110x33, but on the bottom edge, so both
   * land on one line. Coordinates and on-screen previews don't change.
   */
  padToSignatureBox(base64Image: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const boxRatio = SIGNATURE_BOX_WIDTH_PT / SIGNATURE_BOX_HEIGHT_PT;
        const imageRatio = image.width / image.height;
        const canvas = document.createElement('canvas');
        // Wider than the box: add space on top. Taller: add space at the sides
        // (centred, as it was already placed).
        canvas.width = imageRatio >= boxRatio ? image.width : Math.round(image.height * boxRatio);
        canvas.height = imageRatio >= boxRatio ? Math.round(image.width / boxRatio) : image.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Failed to get canvas context');
          return;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // One practically invisible pixel in the top corner keeps the padding
        // from being trimmed away as "empty" when the image is processed.
        ctx.fillStyle = 'rgba(255, 255, 255, 0.02)';
        ctx.fillRect(canvas.width - 1, 0, 1, 1);
        ctx.drawImage(
          image,
          Math.round((canvas.width - image.width) / 2),
          canvas.height - image.height,
        );
        resolve(canvas.toDataURL('image/png'));
      };
      image.onerror = () => reject('Failed to load image');
      image.src = base64Image;
    });
  }

  checkIfImageIsBlank(base64Image: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Image;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        let hasVisibleContent = false;
        for (let i = 0; i < pixels.length; i += 4) {
          const alpha = pixels[i + 3];
          if (alpha !== 0) {
            hasVisibleContent = true;
            break;
          }
        }

        resolve(!hasVisibleContent);
      };

      img.onerror = () => resolve(true);
    });
  }
  addSignature(base64Image: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 500;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Failed to get canvas context');
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height - 20);

        // Left border
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, 20);
        ctx.lineTo(0, canvas.height - 20);
        ctx.stroke();

        // Bottom border
        ctx.beginPath();
        ctx.moveTo(0, canvas.height - 20);
        ctx.stroke();

        ctx.font = '20px Cambria';
        ctx.textAlign = 'right';

        resolve(canvas.toDataURL('image/png'));
      };

      image.onerror = () => {
        reject('Failed to load image');
      };

      image.src = base64Image;
    });
  }
}
